/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2025 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/
import { IAudioFactory } from "./AudioFactory";
import { AssetUrlType, IAssetUrlCache } from "types/AssetUrlCache";
import { AUDIO_EFFECTS, URL_PROVIDING_AUDIO_EFFECTS } from "./AudioEffects";
import {
  AudioEffect,
  AudioEffectClass,
  URLProvidingEffectClass,
} from "types/AudioEffect";
import { ClipId, PlaylistClip } from "types/Clip";
import { createLogger } from "utilities/Logger";
import { UnmixedEffect } from "AudioPlayer/StreamingAudioPlayer/AudioEffects/EnhanceMixEffect";
import { TypedEventTarget } from "typescript-event-target";

export enum StreamingClipState {
  resolvingUrl = "resolvingUrl",
  buffering = "buffering",
  ready = "ready",
  destroyed = "destroyed",
  ended = "ended",
}

interface EffectConstructors {
  urlProvidingEffect: URLProvidingEffectClass;
  otherEffects: AudioEffectClass[];
}

interface StreamingClipEvents {
  error: CustomEvent<{ clipId: ClipId }>;
  waiting: CustomEvent<{ clipId: ClipId }>;
  ready: CustomEvent<{ clipId: ClipId }>;
  ended: CustomEvent<{ clipId: ClipId }>;
}

export class StreamingClip extends TypedEventTarget<StreamingClipEvents> {
  private element?: HTMLAudioElement;
  private sourceNode?: MediaElementAudioSourceNode;

  private urlProvidingEffect: URLProvidingEffectClass = UnmixedEffect;
  private effectChain: AudioEffect[] = [];
  private clip: PlaylistClip;

  private _state: StreamingClipState = StreamingClipState.resolvingUrl;

  private readyPromise: Promise<void> | undefined = undefined;

  constructor(
    private readonly audioContext: AudioContext,
    private readonly audioFactory: IAudioFactory,
    private readonly assetUrlCache: IAssetUrlCache,
    private readonly logger = createLogger("StreamingClip")
  ) {
    super();
  }

  get state(): StreamingClipState {
    return this._state;
  }

  get ready(): Promise<void> {
    return this.readyPromise ?? Promise.reject("Not initialized");
  }

  private static effectConstructors(clip: PlaylistClip): EffectConstructors {
    return {
      urlProvidingEffect:
        URL_PROVIDING_AUDIO_EFFECTS.find((effect) =>
          effect.canBuildFor(clip)
        ) ?? UnmixedEffect,
      otherEffects: AUDIO_EFFECTS.filter((effect) => effect.canBuildFor(clip)),
    };
  }

  async initialize(clip: PlaylistClip, playlistTime: number): Promise<void> {
    if (this.readyPromise) {
      throw new Error("StreamingClip already initialized");
    }

    this.readyPromise = new Promise(async (resolve) => {
      this.clip = clip;
      const { urlProvidingEffect } = StreamingClip.effectConstructors(clip);
      this.urlProvidingEffect = urlProvidingEffect;
      this.logger.info(
        `Getting clip url for urlProvidingEffect ${urlProvidingEffect.name}`
      );
      const url = await this.assetUrlCache.getClipUrl(
        clip,
        urlProvidingEffect,
        AssetUrlType.AUDIO
      );

      this.logger.log("Creating audio element for", url);

      await this.createAudioElement(url, playlistTime, clip);

      resolve();
    });
    return this.readyPromise;
  }

  private async createAudioElement(
    url: string,
    playlistTime: number,
    clip: PlaylistClip
  ): Promise<void> {
    return new Promise((resolve) => {
      const element = this.audioFactory.createAudio();
      element.currentTime = this.calculateElementTime(playlistTime, clip);
      element.src = url;
      element.crossOrigin = "anonymous";
      element.preload = "auto";

      this.element = element;
      this.sourceNode = this.audioContext.createMediaElementSource(element);
      this.sourceNode.connect(this.audioContext.destination); // shouldn't need this but the test wants it

      element.oncanplay = (): void => {
        this.logger.debug("🎵 Clip ready:", {
          clip,
          mediaTime: element.currentTime,
          currentTime: new Date().toISOString(),
        });
        this._state = StreamingClipState.ready;
        this.dispatchTypedEvent(
          "ready",
          new CustomEvent("ready", {
            detail: {
              clipId: clip.id,
            },
          })
        );
        resolve();
      };

      element.onerror = (): void => {
        if (this.state === StreamingClipState.destroyed) {
          return;
        }

        this.assetUrlCache.invalidateClipUrl(
          clip,
          this.urlProvidingEffect,
          AssetUrlType.AUDIO
        );
        this.dispatchTypedEvent(
          "error",
          new CustomEvent("error", {
            detail: {
              clipId: clip.id,
            },
          })
        );
      };

      element.onwaiting = (): void => {
        this._state = StreamingClipState.buffering;
        this.dispatchTypedEvent(
          "waiting",
          new CustomEvent("waiting", {
            detail: {
              clipId: clip.id,
            },
          })
        );
      };

      element.onended = (): void => {
        this._state = StreamingClipState.ended;
        this.dispatchTypedEvent(
          "ended",
          new CustomEvent("ended", {
            detail: {
              clipId: clip.id,
            },
          })
        );
      };

      this._state = StreamingClipState.buffering;
    });
  }

  play(currentTime: number): void {
    if (
      !this.element ||
      !this.sourceNode ||
      this.state !== StreamingClipState.ready
    ) {
      throw new Error("Attempted to play uninitialized audio element");
    }

    if (this.element.ended) {
      return;
    }

    if (this.element.paused) {
      this.logger.log(
        `▶️ _updateProgress() - Playing clip at time ${this.element.currentTime}:`,
        this.clip
      );
      this.setupEffectChain(currentTime);
      this.element.play();
    }
  }

  suspend(): void {
    if (this.element) {
      this.element.pause();
    }
  }

  resume(): void {
    if (this.element) {
      this.element.play();
    }
  }

  pause(): void {
    if (this.element) {
      this.element.pause();
      this.teardownEffectChain();
      this.sourceNode?.disconnect();
    }
  }

  stop(): void {
    this.logger.log("🛑 Stopping clip:", this.clip);
    this.pause();
    this.cleanup();
  }

  private cleanup(): void {
    if (this.element) {
      this.element.pause();
      this.element.src = "";
    }

    if (this.sourceNode) {
      this.sourceNode.disconnect();
    }

    this.element = undefined;
    this.sourceNode = undefined;
    this.teardownEffectChain();
    this._state = StreamingClipState.destroyed;
  }

  private calculateElementTime(
    playlistTime: number,
    clip: PlaylistClip
  ): number {
    const clipTime = Math.max(0, playlistTime - clip.startTime);
    return (clipTime + clip.offset) / 1000; // Convert to seconds
  }

  canHotswap(newClip: PlaylistClip): boolean {
    const { urlProvidingEffect: newUrlProvidingEffect } =
      StreamingClip.effectConstructors(newClip);

    const urlProvidingEffectStable =
      newUrlProvidingEffect === this.urlProvidingEffect;

    return (
      this.element === undefined ||
      (this.element.paused && urlProvidingEffectStable) ||
      this.effectChainHotswappable(newClip)
    );
  }

  hotswap(newClip: PlaylistClip): void {
    if (!this.canHotswap(newClip)) {
      throw new Error("Invalid hotswap attempt");
    }

    this.effectChain.forEach((effect) => {
      effect.hotswap(newClip);
    });
  }

  get currentTimeMs(): number {
    return (this.element?.currentTime ?? 0) * 1000;
  }

  get isPaused(): boolean {
    return this.element?.paused ?? true;
  }

  private teardownEffectChain(): void {
    this.sourceNode?.disconnect();
    let lastEffect;
    while ((lastEffect = this.effectChain.pop())) {
      lastEffect.disconnect();
    }
  }

  private effectChainHotswappable = (newClip: PlaylistClip): boolean => {
    const constructors = StreamingClip.effectConstructors(newClip);
    const newEffects = [
      constructors.urlProvidingEffect,
      ...constructors.otherEffects,
    ];

    if (constructors.urlProvidingEffect !== this.urlProvidingEffect) {
      return false;
    }

    if (newEffects.length !== this.effectChain.length) {
      return false;
    }

    return this.effectChain.every((effect, idx) => {
      const newNodeType = newEffects[idx];
      return effect instanceof newNodeType;
    });
  };

  private setupEffectChain(currentTime: number): void {
    this.teardownEffectChain();

    let lastNode: AudioNode | undefined = undefined;

    const constructors = StreamingClip.effectConstructors(this.clip);
    const newEffects = [
      constructors.urlProvidingEffect,
      ...constructors.otherEffects,
    ];

    for (const Effect of newEffects) {
      const effect = new Effect(this.audioContext, this.clip, currentTime);

      this.effectChain.push(effect);

      lastNode?.connect(effect.inputNode);
      lastNode = effect.outputNode;
    }

    if (this.effectChain.length > 0 && this.sourceNode) {
      this.sourceNode.connect(this.effectChain[0].inputNode);
      lastNode?.connect(this.audioContext.destination);
    }
  }
}
